import React from "react";
import Dialog from "Components/DesignSystem/Dialog/Dialog";
import Button from "Components/DesignSystem/Button/Button";
import TextField from "Components/DesignSystem/TextField/TextField";
import { CANCEL } from "../ARSConstants";

type CopyModalProps = {
    showModal: boolean;
    closeModal: () => void;
    onConfirm: () => void;
    modelName?: string;
    setCopyModelName: (name: string) => void;
    id?: string;
    isCopying?: boolean;
    error?: string;
}

/**
 * The copy-model name prompt on the DS Dialog (§ 96). The body was already DS
 * (TextField carries the label, error state and focus ring — 2026-08-23); the
 * ModalContainer/ModalHeader shell retires here, so Escape and scrim-click now
 * close through the same `closeModal` as Cancel and the X. Shared by ARS logs,
 * the ISR/Guide list + main page, and Lists.
 *
 * § 209 (2026-09-05) — while the parent's copy is in flight (`isCopying`; all
 * three mounts pass a live flag reset in `finally`) the dialog is COMMITTED:
 * the confirm wears the DS loading state and every close route is shut
 * (Cancel disabled, X/Escape no-op) — the copy is not abortable, and a
 * mid-flight close would only hide the dialog while the toast + navigation
 * still land. A FAILED copy lifts the guard (the `finally` reset), which is
 * what keeps ISRMainPage's keep-open-to-fix-the-name flow working.
 */
const CopyModal = ({ showModal, closeModal, onConfirm, modelName, setCopyModelName, id, isCopying = false, error = "" }: CopyModalProps) => {
    const guardedClose = () => {
        if (isCopying) return;
        closeModal();
    };

    return (
        <Dialog
            isOpen={showModal}
            onClose={guardedClose}
            title="Copy Model"
            data-testid={id}
            footer={
                <div className="tw-flex tw-w-full tw-justify-end tw-items-center tw-gap-3">
                    <Button variant="secondary" showIcon={false} data-testid="ars-copy-cancel-btn" disabled={isCopying} onClick={guardedClose}>{CANCEL}</Button>
                    <Button variant="primary" showIcon={false} data-testid="ars-copy-confirm-btn" className="tw-min-w-[88px]" onClick={() => onConfirm()} disabled={!modelName?.trim()} loading={isCopying}>{isCopying ? "Copying" : "Copy"}</Button>
                </div>
            }
        >
            <TextField
                label="Model Name"
                value={modelName ?? ""}
                onChange={setCopyModelName}
                error={!!error}
                helperText={error || undefined}
                data-testid="components-copymodal-input-1"
            />
        </Dialog>
    )
}

export default CopyModal;
